import { useCallback, useEffect, useMemo, useState } from "react";
import { WorkoutProfileResponseType, WorkoutProfileType } from "@WahooFitness/cloud-client-types";
import {
  useCloudContext,
  useConfigContext,
  useGuardedUpdater,
  useOfflineSWR,
  usePubSubContext,
  useUserContext,
} from "@WahooFitness/wahoo-offline-mfe";
import { WorkoutProfiles } from "@WahooFitness/cloud-client-ts";
import { WSMWorkoutProfile } from "@WahooFitness/wsm-native/dist/esm/types/workout_profile";
import { getWorkoutPagesData } from "@/services/getWorkoutPagesData";
import { IWorkoutProfilesSource, sortWorkoutProfiles } from "./useWorkoutProfilesSource";
import { t } from "@lingui/macro";
import { debounce } from "lodash";

function makeWsmWorkoutProfile(p: WorkoutProfileResponseType): WSMWorkoutProfile {
  return {
    ...p,
    external_id: p.external_id ?? `${p.id}`,
    "#dbId": p.id,
    "#deleted": false,
    token: p.external_id ?? `${p.id}`,
  };
}

export const useCloudWorkoutProfilesSource = (enabled: boolean): IWorkoutProfilesSource => {
  const { wahooToken } = useConfigContext();
  const { currentUser, userIsLoading } = useUserContext();
  const { update, updateInProgress } = useGuardedUpdater();
  const { getCloudClient } = useCloudContext();
  const { subscribeToWorkoutProfileUpdates } = usePubSubContext();
  const [ignoreWorkoutProfileUpdates, setIgnoreWorkoutProfileUpdates] = useState(false);

  const workoutProfilesClient = useMemo(() => getCloudClient(WorkoutProfiles), [getCloudClient]);

  const {
    data: workoutProfiles,
    isLoading: workoutProfilesAreLoading,
    error: workoutProfilesError,
    mutate: reloadWorkoutProfiles,
  } = useOfflineSWR(
    enabled && ["workoutProfiles", wahooToken],
    async ([_key, token]): Promise<WSMWorkoutProfile[]> => {
      const result = await workoutProfilesClient.get(token, { loadWorkoutAlerts: true });
      return result.map(makeWsmWorkoutProfile).sort(sortWorkoutProfiles);
    }
  );

  const getWorkoutProfile = useCallback(
    (workoutProfileExternalId: string) => {
      return workoutProfiles?.find((p) => p.external_id === workoutProfileExternalId);
    },
    [workoutProfiles]
  );

  const wsmToCloudProfileMap = useMemo(() => {
    const map: Record<string, number> = {};
    workoutProfiles?.forEach((p) => {
      map[p.external_id] = p.id;
    });
    return map;
  }, [workoutProfiles]);

  useEffect(() => {
    if (enabled && !userIsLoading && currentUser) {
      const subscription = subscribeToWorkoutProfileUpdates(currentUser.id, () => {
        if (!ignoreWorkoutProfileUpdates) {
          reloadWorkoutProfiles();
        }
      });
      return () => {
        subscription.unsubscribe();
      };
    }
    return () => {};
  }, [
    enabled,
    currentUser,
    ignoreWorkoutProfileUpdates,
    reloadWorkoutProfiles,
    subscribeToWorkoutProfileUpdates,
    userIsLoading,
  ]);

  const createWorkoutProfile = useCallback(
    (workoutProfile: WorkoutProfileType): Promise<WSMWorkoutProfile | null> => {
      return update(
        async () => {
          const result = await workoutProfilesClient.createWorkoutProfile(
            workoutProfile,
            wahooToken
          );
          return makeWsmWorkoutProfile(result);
        },
        {
          errorMessage: t`Something went wrong creating the profile. Please check your network and try again.`,
        }
      );
    },
    [update, wahooToken, workoutProfilesClient]
  );

  const editWorkoutProfile = useCallback(
    (
      workoutProfileExternalId: string,
      edits: Partial<WorkoutProfileType>
    ): Promise<WSMWorkoutProfile | null> => {
      return update(
        async () => {
          const cloudProfileId = wsmToCloudProfileMap[workoutProfileExternalId];
          if (cloudProfileId === undefined) {
            throw new Error("Workout profile not found");
          }
          const result = await workoutProfilesClient.updateWorkoutProfile(
            cloudProfileId,
            edits,
            wahooToken
          );
          return makeWsmWorkoutProfile(result);
        },
        {
          errorMessage: t`Something went wrong editing the profile. Please check your network and try again.`,
        }
      );
    },
    [update, wahooToken, workoutProfilesClient, wsmToCloudProfileMap]
  );

  const deleteWorkoutProfile = useCallback(
    (workoutProfileExternalId: string) => {
      return update(
        async () => {
          const cloudProfileId = wsmToCloudProfileMap[workoutProfileExternalId];
          if (cloudProfileId === undefined) {
            throw new Error("Workout profile not found");
          }
          await workoutProfilesClient.delete(cloudProfileId, wahooToken);
          return true;
        },
        {
          errorMessage: t`Something went wrong deleting the profile. Please check your network and try again.`,
        }
      );
    },
    [update, wahooToken, workoutProfilesClient, wsmToCloudProfileMap]
  );

  const resetWorkoutProfile = useCallback(
    (workoutProfileExternalId: string) => {
      return update(
        async () => {
          const cloudProfileId = wsmToCloudProfileMap[workoutProfileExternalId];
          if (cloudProfileId === undefined) {
            throw new Error("Workout profile not found");
          }
          const result = await workoutProfilesClient.reset(cloudProfileId, wahooToken);
          return makeWsmWorkoutProfile(result.workout_profile);
        },
        {
          errorMessage: t`Something went wrong resetting the profile. Please check your network and try again.`,
        }
      );
    },
    [update, wahooToken, workoutProfilesClient, wsmToCloudProfileMap]
  );

  const updateWorkoutProfilePagesImmediately = useCallback(
    (workoutProfileExternalId: string, pages: Uint8Array) => {
      return update(
        async () => {
          let cloudProfileId = wsmToCloudProfileMap[workoutProfileExternalId];
          if (cloudProfileId === undefined) {
            // It's probably new, so the cloud ID will be the external ID
            cloudProfileId = Number(workoutProfileExternalId);
          }
          await workoutProfilesClient.updateWorkoutProfileWorkoutPagesFile(
            cloudProfileId,
            Buffer.from(pages),
            wahooToken
          );
        },
        {
          errorMessage: t`Something went wrong updating the profile pages. Please check your network and try again.`,
        }
      );
    },
    [update, wahooToken, workoutProfilesClient, wsmToCloudProfileMap]
  );

  const updateWorkoutProfilePagesDebounce = useMemo(
    () => debounce(updateWorkoutProfilePagesImmediately, 5000),
    [updateWorkoutProfilePagesImmediately]
  );

  const getWorkoutProfilePages = useCallback(
    async (workoutProfileExternalId: string) => {
      const workoutProfile = getWorkoutProfile(workoutProfileExternalId);
      if (!workoutProfile?.workout_pages_file?.url) {
        return;
      }
      return getWorkoutPagesData(workoutProfile.workout_pages_file.url);
    },
    [getWorkoutProfile]
  );

  return {
    workoutProfiles,
    workoutProfilesError,
    workoutProfilesAreLoading,
    workoutProfileUpdateInProgress: updateInProgress,
    reloadWorkoutProfiles,
    getWorkoutProfile,
    createWorkoutProfile,
    editWorkoutProfile,
    deleteWorkoutProfile,
    resetWorkoutProfile,
    updateWorkoutProfilePagesImmediately,
    updateWorkoutProfilePagesDebounce,
    getWorkoutProfilePages,
    setIgnoreWorkoutProfileUpdates,
  };
};
